import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { executeQuery } from '../../graphql';
import { getRestyleCheckStatus } from '../../graphql/queries';
import Wizard from '../../components/Wizard';
import InfoBox from '../../components/InfoBox';
import Button from '../../components/Button';
import { usePageContent } from '../../hooks/usePageContent';
import styles from './index.module.css';

const RestyleComboPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { find } = usePageContent('restyle-combo');
  const wizardContent = find('wizard')       || {};
  const c             = find('comboChecker') || {};
  const wizardSteps = wizardContent.steps || [
    { title: 'Check combination' },
    { title: 'Select a style' },
    { title: 'Confirm ownership' },
    { title: 'Checkout' },
  ];

  const urlParams = new URLSearchParams(location.search);
  const initialCombo = urlParams.get('combination') || '';
  const initialVehicleType = urlParams.get('vehicleType') || 'car';

  const [vehicleType, setVehicleType] = useState(initialVehicleType);
  const [combo, setCombo] = useState(initialCombo);
  const [loading, setLoading] = useState(false);
  const [searchReturned, setSearchReturned] = useState(false);
  const [success, setSuccess] = useState(false);
  const [failedTest, setFailedTest] = useState('');
  const [comboError, setComboError] = useState('');
  const [restyleStatus, setRestyleStatus] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('combination')) {
      setCombo('');
      setVehicleType('car');
      setSearchReturned(false);
      setSuccess(false);
      setFailedTest('');
      setComboError('');
      setRestyleStatus('');
    }
    sessionStorage.removeItem('plateOrderData');
  }, [location]);

  const isCar = vehicleType === 'car';
  const maxLength  = isCar ? (c.maxCharsCar || 6) : (c.maxCharsMotorcycle || 5);
  const minLength  = c.minChars || 2;
  const placeholder   = isCar ? (c.placeholderCar || 'ABC456') : (c.placeholderMotorcycle || 'MOTO5');
  const instructions  = isCar ? (c.instructionsCar || 'Try 2 to 6 letters and/or a combination of letters and number') : (c.instructionsMotorcycle || 'Try 2 to 5 letters and/or a combination of letters and number');
  const comboErrorText = isCar ? (c.errorCar || 'Enter between 2 and 6 letters/numbers only') : (c.errorMotorcycle || 'Enter between 2 and 5 letters/numbers only');
  const infoInitial  = c.infoInitial  || {};
  const infoFormat   = c.infoFormat   || {};
  const infoAvailable = c.infoAvailable || {};

  const handleVehicleTypeChange = (type) => {
    setVehicleType(type);

    // Re-validate the current combo against the NEW vehicle type's constraints
    const newIsCar = type === 'car';
    const newMaxLength = newIsCar ? (c.maxCharsCar || 6) : (c.maxCharsMotorcycle || 5);
    const newComboErrorText = newIsCar ? (c.errorCar || 'Enter between 2 and 6 letters/numbers only') : (c.errorMotorcycle || 'Enter between 2 and 5 letters/numbers only');
    const regex = /^[0-9a-zA-Z]*$/;

    if (combo.length > 0 && (!regex.test(combo) || combo.length < minLength || combo.length > newMaxLength)) {
      setFailedTest('format');
      setComboError(newComboErrorText);
      setSearchReturned(false);
      setSuccess(false);
      setRestyleStatus('');
    } else if (failedTest === 'availability') {
      // Preserve unavailable state — the combo was already checked and is not available.
      // User must still see the error message and action buttons.
    } else {
      setFailedTest('');
      setComboError('');
      setSearchReturned(false);
      setSuccess(false);
      setRestyleStatus('');
    }
  };

  const validateComboInput = (value) => {
    const regex = /^[0-9a-zA-Z]*$/;
    if (!regex.test(value)) {
      setComboError(comboErrorText);
      setFailedTest('format');
      return false;
    }
    setComboError('');
    setFailedTest('');
    return true;
  };

  const handleComboChange = (e) => {
    const value = e.target.value.toUpperCase();
    if (value.length <= maxLength) {
      setCombo(value);
      setSearchReturned(false);
      setSuccess(false);
      setRestyleStatus('');
      if (value.length > 0) {
        validateComboInput(value);
      } else {
        setComboError('');
        setFailedTest('');
      }
    }
  };

  const checkComboAvailability = async () => {
    if (combo.length < minLength || combo.length > maxLength || failedTest === 'format') {
      return;
    }

    setLoading(true);
    setSearchReturned(false);

    try {
      const vehicleTypeId = vehicleType === 'car' ? 1 : 2;
      const result = await executeQuery(getRestyleCheckStatus, {
        PlateCombo: combo,
        VehicleType: vehicleTypeId,
        SalesChannel: 2 // Restyle channel
      });

      console.log('Plate validation result for restyle:', result);

      if (result.getRestyleCheckStatus) {
        const status = result.getRestyleCheckStatus.Status;
        setSearchReturned(true);

        // For restyle, we need to check if the plate exists and can be restyled
        // Status codes might be different for restyle
        if (status === 1) {
          setSuccess(true);
          setRestyleStatus('available');
          setFailedTest('');
        } else if (status === 7) {
          setSuccess(false);
          setRestyleStatus('doesnotexist');
          setFailedTest('availability');
        } else {
          setSuccess(false);
          setRestyleStatus('notavailable');
          setFailedTest('availability');
        }
      }
    } catch (error) {
      console.error('Error validating plate for restyle:', error);
      setSearchReturned(true);
      setSuccess(false);
      setRestyleStatus('notavailable');
      setFailedTest('availability');
    } finally {
      setLoading(false);
    }
  };

  const resetCombo = () => {
    setCombo('');
    setSearchReturned(false);
    setSuccess(false);
    setFailedTest('');
    setComboError('');
    setRestyleStatus('');
    sessionStorage.removeItem('plateOrderData');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (success) {
      sessionStorage.setItem('plateOrderData', JSON.stringify({ combination: combo, vehicleType }));
      // Replace the current history entry so the browser back button returns here with the combo pre-filled
      const backUrl = `/restyle/check-combination?combination=${encodeURIComponent(combo)}&vehicleType=${encodeURIComponent(vehicleType)}`;
      window.history.replaceState(null, '', backUrl);
      navigate('/restyle/select-a-style');
    }
  };

  const handleCreatePlate = () => {
    sessionStorage.setItem('plateOrderData', JSON.stringify({ combination: combo, vehicleType }));
    // Replace the current history entry so the browser back button returns here with the combo pre-filled
    const backUrl = `/restyle/check-combination?combination=${encodeURIComponent(combo)}&vehicleType=${encodeURIComponent(vehicleType)}`;
    window.history.replaceState(null, '', backUrl);
    navigate('/create/select-a-style');
  };

  return (
    <div id="content" className={styles.page}>
      <div className="row">
        <div className="page-header page-header--small">
          <div className="page-header__content">
            <h1 className="page-header__title">{c.pageTitle || 'Restyle your existing plates'}</h1>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="plate-wizard">
          {/* Progress Stepper */}
          <Wizard
            steps={wizardSteps}
            currentStep={0}
          />

          {/* Form Page: Combo */}
          <div className="plate-wizard__form plate-wizard__form--combo">
            <div className="plate-wizard__form-header">
              <h2 className="plate-wizard__form-title" tabIndex="-1">
                {c.sectionTitle || 'Check combination'}
              </h2>
            </div>

            <div className="check-combo">
              <form
                action="/restyle/select-a-style"
                method="get"
                autoComplete="off"
                className={`combination-checker ${loading ? 'loading' : ''}`}
                onSubmit={handleSubmit}
              >
                {/* Loading Panel */}
                {loading && <div className="loading__panel">{c.loadingText || 'Loading...'}</div>}

                <div className="combination-checker__container-inner combination-checker__container-inner--ready">
                  <div className="combination-checker__columns">
                    {/* Vehicle Type Column */}
                    <div className="combination-checker__column combination-checker__column--vehicle">
                      <fieldset
                        className="combination-checker__graphic-radio"
                        disabled={loading || (searchReturned && success)}
                      >
                        <legend className="combination-checker__label">
                          {c.vehicleTypeLabel || 'Select your vehicle type'}
                        </legend>
                        <div className="combination-checker__graphic-radio-options">
                          <div className={`combination-checker__graphic-radio-option${searchReturned && vehicleType !== 'car' ? ' combination-checker__graphic-radio-option--greyed' : ''}`}>
                            <input
                              className="combination-checker__graphic-radio-option-input visuallyhidden visuallyhidden--no-focus"
                              type="radio"
                              name="wizard-check__type"
                              id="wizard-check__type-car"
                              value="car"
                              checked={vehicleType === 'car'}
                              onChange={() => handleVehicleTypeChange('car')}
                            />
                            <label
                              className="combination-checker__graphic-radio-option-label"
                              htmlFor="wizard-check__type-car"
                            >
                              <span className="combination-checker__graphic-radio-option-label-text">
                                {c.carLabel || 'Car'}
                              </span>
                              <span
                                className="combination-checker__graphic-radio-option-label-image-holder"
                                aria-hidden="true"
                              >
                                <img
                                  className="combination-checker__graphic-radio-option-label-image combination-checker__graphic-radio-option-label-image--car"
                                  src="/images/icons/car--tree-green.svg"
                                  alt=""
                                />
                              </span>
                            </label>
                          </div>
                          <div className={`combination-checker__graphic-radio-option${searchReturned && vehicleType !== 'motorcycle' ? ' combination-checker__graphic-radio-option--greyed' : ''}`}>
                            <input
                              className="combination-checker__graphic-radio-option-input visuallyhidden visuallyhidden--no-focus"
                              type="radio"
                              name="wizard-check__type"
                              id="wizard-check__type-motorcycle"
                              value="motorcycle"
                              checked={vehicleType === 'motorcycle'}
                              onChange={() => handleVehicleTypeChange('motorcycle')}

                            />
                            <label
                              className="combination-checker__graphic-radio-option-label"
                              htmlFor="wizard-check__type-motorcycle"
                            >
                              <span className="combination-checker__graphic-radio-option-label-text">
                                {c.motorcycleLabel || 'Motorbike'}
                              </span>
                              <span
                                className="combination-checker__graphic-radio-option-label-image-holder"
                                aria-hidden="true"
                              >
                                <img
                                  className="combination-checker__graphic-radio-option-label-image combination-checker__graphic-radio-option-label-image--motorcycle"
                                  src="/images/icons/motorcycle--tree-green.svg"
                                  alt=""
                                />
                              </span>
                            </label>
                          </div>
                        </div>
                      </fieldset>
                      <input type="hidden" value={vehicleType} name="vehicleType" />
                    </div>

                    {/* Info Column */}
                    <div className="combination-checker__column combination-checker__column--info" tabIndex="-1">
                      {/* Initial Info Box */}
                      {!searchReturned && failedTest !== 'format' && (
                        <InfoBox
                          title={infoInitial.title || 'Suggested plate combinations'}
                          icon={infoInitial.icon || 'info'}
                          className="info-box--max-450"
                        >
                          <p>{infoInitial.introText || "Here's a few examples:"}</p>
                          {infoInitial.items?.length > 0
                            ? <ul>{infoInitial.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
                            : <ul><li>ABC.123</li><li>ABCDEF</li><li>A8ABC</li><li>11.ABCD</li></ul>
                          }
                          <p>{infoInitial.paragraph1 || 'Consider using a 1 instead of an I or a 5 instead of an S. If that doesn\'t work, try a 3 in place of an E, or even a 4 as an A.'}</p>
                          <p>{infoInitial.paragraph2 || "Keep in mind, some styles have certain combo rules. If you have a look in mind, start by checking out the styles first, to see which combo's work in that style."}</p>
                        </InfoBox>
                      )}

                      {/* Format Error Info Box */}
                      {!success && failedTest === 'format' && (
                        <InfoBox
                          title={infoFormat.title || 'This combination is not valid.'}
                          icon={infoFormat.icon || 'error'}
                          className="info-box--max-450"
                        >
                          <p>{infoFormat.paragraph1 || 'Please try alternative plate combinations.'}</p>
                          <p>{infoFormat.paragraph2 || 'Your combination should include letters and numbers, for example:'}</p>
                          {infoFormat.items?.length > 0
                            ? <ul>{infoFormat.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
                            : <ul><li>AAA000</li><li>1AA1AA</li><li>ABC123</li></ul>
                          }
                        </InfoBox>
                      )}

                      {/* Available for Restyle Info Box */}

                      {/* Simple Messages for Restyle Status */}
                      {searchReturned && restyleStatus === 'available' && (
                        <p className="combination-checker__simple-message combination-checker__simple-message--available">
                          {c.simpleAvailable || 'Congratulations! This plate combo can be restyled'}
                        </p>
                      )}

                      {searchReturned && restyleStatus === 'doesnotexist' && (
                        <p className="combination-checker__simple-message combination-checker__simple-message--not-available">
                          {c.simpleDoesNotExist || 'This combination is not owned yet'}
                        </p>
                      )}

                      {searchReturned && restyleStatus === 'notavailable' && (
                        <p className="combination-checker__simple-message combination-checker__simple-message--not-available">
                          {c.simpleNotAvailable || 'Sorry, this plate combo cannot be restyled'}
                        </p>
                      )}
                    </div>

                    {/* Combo Input Column */}
                    <div className="combination-checker__column combination-checker__column--combo">
                      <h3 className="combination-checker__label" aria-hidden="true">
                        {c.comboLabel || 'Enter your existing plate combo'}
                      </h3>
                      <div className="combination-checker__combo">
                        <div
                          className={`combination-checker__combo-text ${
                            failedTest === 'format' || success ? 'visuallyhidden' : ''
                          }`}
                        >
                          <label
                            className="combination-checker__label combination-checker__combo-text-instructions"
                            htmlFor="combination"
                          >
                            <span className="visuallyhidden">Your plate combination. </span>
                            <span aria-hidden={loading || (searchReturned && success)}>
                              {instructions}
                            </span>
                          </label>
                          <div className="combination-checker__combo-text-count" id="combination-checker__count" aria-hidden="true">
                            <h3 className="visuallyhidden">Character count</h3>
                            {combo.length}/{maxLength}
                          </div>
                        </div>
                        <div
                          className={`combination-checker__combo-container ${
                            searchReturned && success ? 'combination-checker__combo-container--ticked' : ''
                          } ${
                            searchReturned && (restyleStatus === 'doesnotexist' || restyleStatus === 'notavailable')
                              ? 'combination-checker__combo-container--crossed'
                              : ''
                          }`}
                        >
                          <input
                            readOnly={searchReturned && success}
                            disabled={loading}
                            className="combination-checker__combo-input"
                            value={combo}
                            id="combination"
                            name="combination"
                            type="text"
                            spellCheck="false"
                            onChange={handleComboChange}
                            placeholder={placeholder}
                            maxLength={maxLength}
                          />
                        </div>
                        {comboError && (
                          <div className="combination-checker__combo-error" role="alert">
                            <p className="combination-checker__combo-error-msg">{comboError}</p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="combination-checker__actions">
                        {restyleStatus !== 'doesnotexist' && (!searchReturned || (searchReturned && !success)) && (
                          <Button
                            type="button"
                            fullWidth
                            onClick={checkComboAvailability}
                            disabled={loading || failedTest !== '' || combo.length < minLength}
                            loading={loading}
                          >
                            {c.checkAvailabilityText || 'Check availability'}
                          </Button>
                        )}

                        {searchReturned && success && (
                          <Button
                            type="submit"
                            fullWidth
                            disabled={!success || combo.length < minLength}
                          >
                            {c.choosePlateStyleText || 'Choose your plate style'}
                          </Button>
                        )}

                        {searchReturned && restyleStatus === 'doesnotexist' && (
                          <Button
                            type="button"
                            fullWidth
                            onClick={handleCreatePlate}
                          >
                            {c.createThisPlateText || 'Create this plate'}
                          </Button>
                        )}

                        {(searchReturned || (initialCombo && combo)) && (
                          <Button
                            type="button"
                            variant="tertiary"
                            className="button--icon-right button--tertiary-grass"
                            onClick={resetCombo}
                            disabled={loading}
                          >
                            {c.startAgainText || 'Reset'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestyleComboPage;
